import { KeyboardArrowDown, KeyboardControlKey } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DateCalendar } from '@mui/x-date-pickers';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { WarningIconTherapyTemplate } from './commonComponent/therapy-planning-icons';
import { useDispatch, useSelector } from 'react-redux';
import { saveTherapyTemplatesAction } from 'src/redux/actions/therapyPlanning';
import { LoadingButton } from '@mui/lab';

type Props = {
  therapyType: string;
  close: any;
  dischargeDate: any;
  getTherapyTemplateCell: any;
};

export const BasicDateCalendar = ({
  handleChangeDate,
  value,
  futureDisable = false,
  disablePast = false,
  minDate,
  maxDate,
}: any) => {
  const jsDate = moment(value, 'DD-MM-YYYY').toDate();
  return (
    <DateCalendar
      sx={{
        boxShadow: '0px 3px 14px 0px rgba(0, 0, 0, 0.08)',
        position: 'fixed',
        background: '#fff',
        zIndex: 10,
        marginTop: '43px',
        width: '280px',
      }}
      defaultValue={jsDate}
      onChange={handleChangeDate}
      minDate={!!minDate ? minDate : null}
      maxDate={!!maxDate ? maxDate : null}
    />
  );
};

const TherapyPlanningTemplate_2_0 = (props: Props) => {
  const dispatch = useDispatch();
  const { therapyType, close, getTherapyTemplateCell, dischargeDate } = props;
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const applyTheTherapyTemplate = useSelector(
    (state: any) => state.applyTheTherapyTemplatesReducer
  );
  const [openCalender, setOpenCalender] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const calenderRef = useRef<HTMLDivElement | null>(null);

  const [dischargeCheckBox, setDischargeCheckBox] = useState<boolean>(false);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDischargeCheckBox(event.target.checked);
  };

  useEffect(() => {
    if (openCalender) {
      const handleClickOutside = (event: any) => {
        if (calenderRef.current && !calenderRef.current.contains(event.target)) {
          handleClickCalender();
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [openCalender]);

  const handleClickCalender = () => {
    setOpenCalender((prev: any) => !prev);
  };
  const handleChangeDate = (e: any) => {
    const newDate = moment(e);
    setSelectedDate(newDate.toDate());
    setOpenCalender(false);
  };

  function getNextWeekDate(date: any) {
    return moment(date).add(1, 'week').format('YYYY-MM-DD');
  }

  const handleSaveTherapyTemplate = () => {
    const currentDate = moment(new Date()).format('YYYY-MM-DD')
    dispatch(
      saveTherapyTemplatesAction({
        patientId: selectedPatient?.uuid,
        params: {
          setToMax: dischargeCheckBox,
          discipline: therapyType,
          startDate: getTherapyTemplateCell?.date,
          toDate: dischargeCheckBox ? null : moment(selectedDate).format('YYYY-MM-DD'),
        },
      })
    );
  };

  useEffect(() => {
    if (!applyTheTherapyTemplate?.isTemplateUpdated && applyTheTherapyTemplate?.response === null)
      return;
    if (applyTheTherapyTemplate?.isTemplateUpdated && applyTheTherapyTemplate?.response !== null) {
      close();
    }
    if (!applyTheTherapyTemplate?.isTemplateUpdated && applyTheTherapyTemplate?.response !== null) {
      close();
    }
  }, [applyTheTherapyTemplate]);

  const getNinthDay = (currentDate: any) => {
    const particularDate = moment(currentDate).startOf('day').add(96, 'days');
    return particularDate.format('YYYY-MM-DD');
  };

  return (
    <>
      <Box p={1}>
        <>
          <DialogContent>
            <Stack
              flexDirection={'column'}
              rowGap={2}
            >
              <Stack
                sx={{ display: 'flex' }}
                mb={3}
              >
                <Typography variant="h5">Apply Weekly Plan</Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    marginTop: 1.5,
                  }}
                >
                  The plan will be applied starting the following week
                </Typography>
              </Stack>
              <Stack
                ref={calenderRef}
                sx={{
                  position: 'relative',
                }}
              >
                <Button
                  sx={{
                    borderRadius: '12px',
                    border: '1px solid rgba(56, 66, 80, 0.50)',
                    padding: '6px 16px',
                    color: '#384250',
                    fontSize: '14px',
                    fontWeight: 400,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                  onClick={handleClickCalender}
                  disabled={dischargeCheckBox}
                  endIcon={openCalender ? <KeyboardControlKey /> : <KeyboardArrowDown />}
                >
                  To {selectedDate ? `(${moment(selectedDate).format('MMM D, YYYY')})` : null}
                </Button>
                {openCalender && (
                  <BasicDateCalendar
                    value={selectedDate === null ? new Date() : selectedDate}
                    handleChangeDate={handleChangeDate}
                    minDate={new Date(getNextWeekDate(getTherapyTemplateCell?.date))}
                    maxDate={
                      dischargeDate
                        ? new Date(dischargeDate)
                        : new Date(getNinthDay(new Date(getTherapyTemplateCell?.date)))
                    }
                  />
                )}
              </Stack>
              <Stack>
                <FormControlLabel
                  sx={{ zIndex: 8 }}
                  control={
                    <Checkbox
                      onChange={handleValueChange}
                      value={dischargeCheckBox}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        textWrap: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Set to Max. (Either the Anticipated DC or up to 90 days)
                    </Typography>
                  }
                />
              </Stack>
              <Stack
                flexDirection={'row'}
                gap={2}
              >
                <Tooltip
                  placement="bottom-start"
                  title={`Applying the template will override existing planned ${therapyType} minutes`}
                >
                  <Box
                    sx={{
                      borderRadius: '10px',
                      background: 'rgba(99, 102, 241, 0.20)',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <WarningIconTherapyTemplate />
                  </Box>
                </Tooltip>
                <Typography>
                  Note: Applying the plan will override existing planned minutes
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              variant="text"
              sx={{ color: '#111927' }}
              onClick={close}
            >
              Cancel
            </Button>
            <LoadingButton
              autoFocus
              variant="contained"
              onClick={handleSaveTherapyTemplate}
              loading={applyTheTherapyTemplate?.loading}
              disabled={!selectedDate && !dischargeCheckBox}
            >
              Apply Plan
            </LoadingButton>
          </DialogActions>
        </>
      </Box>
    </>
  );
};

export default TherapyPlanningTemplate_2_0;
